import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { MediaItem } from "@/types";
import { typeLabels, typePaths } from "@/lib/labels";
import PlayModal from "@/components/feature/PlayModal";

interface HeroCarouselProps {
  items: MediaItem[];
}

export default function HeroCarousel({ items }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);
  const [playOpen, setPlayOpen] = useState(false);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setIndex((i) => (i + 1) % items.length);
    }, 6000);
    return () => window.clearInterval(timer);
  }, [items.length]);

  if (items.length === 0) return null;
  const active = items[index];

  return (
    <section className="relative h-[520px] w-full overflow-hidden md:h-[640px]">
      {items.map((item, i) => (
        <div
          key={item.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === index ? "opacity-100" : "opacity-0"
          }`}
        >
          <img
            src={item.backdrop}
            alt={`${item.title} 剧照`}
            title={`${item.title}-榴莲视频焦点推荐`}
            className={`h-full w-full object-cover ${i === index ? "animate-zoom-slow" : ""}`}
          />
        </div>
      ))}

      <div className="absolute inset-0 bg-gradient-to-r from-background-50 via-background-50/85 to-background-50/30"></div>
      <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-transparent to-background-50/60"></div>

      <div className="relative z-10 mx-auto flex h-full max-w-[1280px] flex-col justify-center px-4 md:px-8">
        <div key={active.id} className="max-w-2xl animate-fade-up">
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-md bg-accent-500 px-2.5 py-1 text-xs font-medium text-background-50">
              焦点推荐
            </span>
            <Link
              to={typePaths[active.type]}
              className="rounded-md bg-primary-500/90 px-2.5 py-1 text-xs text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
            >
              {typeLabels[active.type]}
            </Link>
            <span className="flex items-center gap-1 text-xs text-foreground-500">
              <i className="ri-star-fill text-accent-500"></i>
              {active.rating.toFixed(1)}
            </span>
          </div>

          <h1 className="mt-4 font-heading text-3xl font-bold leading-tight text-foreground-950 md:text-5xl">
            {active.title}
          </h1>
          <p className="mt-2 font-heading text-sm tracking-wide text-foreground-400 md:text-base">
            {active.originalTitle}
          </p>

          <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-foreground-400">
            <span>{active.year}</span>
            <span className="h-3 w-px bg-background-300"></span>
            <span>{active.region}</span>
            <span className="h-3 w-px bg-background-300"></span>
            <span>{active.duration}</span>
            <span className="h-3 w-px bg-background-300"></span>
            <span>{active.genres.join(" · ")}</span>
          </div>

          <p className="mt-4 max-w-xl dv-clamp-3 text-sm leading-relaxed text-foreground-400 md:text-base">
            {active.synopsis}
          </p>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => setPlayOpen(true)}
              className="flex items-center gap-2 rounded-md bg-accent-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-accent-600 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-play-fill text-lg"></i>
              立即播放
            </button>
            <Link
              to={`/detail/${active.id}`}
              className="flex items-center gap-2 rounded-md border border-background-300/70 bg-background-100/50 px-6 py-3 text-sm text-foreground-700 backdrop-blur-sm transition-colors hover:border-primary-400 hover:text-foreground-950 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-information-line text-lg"></i>
              查看详情
            </Link>
          </div>
        </div>
      </div>

      <div className="absolute bottom-6 right-4 z-10 flex items-center gap-3 md:right-8">
        <div className="flex items-center gap-2">
          {items.map((item, i) => (
            <button
              key={item.id}
              type="button"
              onClick={() => setIndex(i)}
              className={`h-1.5 rounded-full transition-all cursor-pointer ${
                i === index ? "w-8 bg-accent-500" : "w-3 bg-background-400/60 hover:bg-background-500"
              }`}
              aria-label={`切换到 ${item.title}`}
            ></button>
          ))}
        </div>
      </div>

      <PlayModal open={playOpen} title={active.title} onClose={() => setPlayOpen(false)} />
    </section>
  );
}